import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleJsonLd } from '@/lib/seo';
import { kaspreadsheetUrl } from '@/mocks/categories';

export default function BaseTaoShippingCost() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <JsonLd data={buildArticleJsonLd({ headline: 'BaseTao Shipping Cost Guide 2026: Complete International Shipping Price Breakdown', description: 'Understand how BaseTao shipping costs are calculated, compare carrier options, and discover money-saving consolidation strategies.', path: '/basetao-shipping-cost' })} />
      <Navbar />
      <main className="pt-20">
        <article>
          <header className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-foreground-700">Shipping Cost</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                BaseTao Shipping Cost Guide 2026: Complete International Shipping Price Breakdown
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed mb-6">
                Understand exactly how BaseTao shipping costs are calculated and discover proven strategies to minimize your international shipping expenses. Covers all carrier options, rate factors, consolidation benefits, and money-saving techniques.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Updated: August 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>13 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="space-y-10 text-foreground-700">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">How BaseTao Shipping Costs Are Calculated</h2>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  International shipping costs through BaseTao are determined by several interconnected factors: the dimensional weight of your package (calculated from length, width, and height), the actual physical weight, the shipping destination country, and the selected carrier service level. The platform&apos;s shipping calculator automatically analyzes all of these variables and provides accurate cost estimates across multiple carriers — typically including standard shipping, express delivery, and premium options.
                </p>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  A commonly overlooked aspect of international shipping is dimensional weight pricing. Carriers charge based on whichever is greater between actual weight and dimensional weight. This means that lightweight but bulky items — such as large jackets or oversized bags — can cost significantly more to ship than their physical weight would suggest. BaseTao Spreadsheet automatically calculates both values and uses the appropriate pricing basis, giving you accurate estimates that match what you will actually be charged.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Shipping Methods and Estimated Costs</h2>
                <div className="overflow-x-auto rounded-lg border border-background-200/60 mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Shipping Method</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Estimated Cost (per kg)</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Delivery Speed</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">Best For</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200/60">
                      {[
                        { method: 'Standard Economy', cost: '$8-15', speed: '15-30 days', best: 'Non-urgent bulk orders' },
                        { method: 'Standard Air', cost: '$15-25', speed: '10-20 days', best: 'Regular shipments' },
                        { method: 'Express Air', cost: '$25-40', speed: '5-10 days', best: 'Time-sensitive orders' },
                        { method: 'Premium Courier', cost: '$40-60', speed: '3-7 days', best: 'Urgent small packages' },
                      ].map((row) => (
                        <tr key={row.method} className="hover:bg-background-50 transition-colors">
                          <td className="px-4 py-2.5 font-medium text-foreground-800 whitespace-nowrap">{row.method}</td>
                          <td className="px-4 py-2.5 text-foreground-600 whitespace-nowrap">{row.cost}</td>
                          <td className="px-4 py-2.5 text-foreground-600 whitespace-nowrap">{row.speed}</td>
                          <td className="px-4 py-2.5 text-foreground-600">{row.best}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Money-Saving Shipping Strategies</h2>
                <div className="space-y-4">
                  {[
                    { title: 'Consolidate Multiple Orders', desc: 'The single most effective way to reduce per-unit shipping costs. Instead of shipping items individually as they arrive at the warehouse, use the consolidation feature to group multiple products into one larger shipment. The per-kilogram rate for consolidated shipments is significantly lower than the rate for individual packages.' },
                    { title: 'Leverage Warehouse Storage Windows', desc: 'Partner warehouses typically offer free storage for a limited period. Use the warehouse dashboard in BaseTao Spreadsheet to track storage durations and plan your consolidation shipments before storage fees begin accruing, maximizing the free storage window.' },
                    { title: 'Ship During Off-Peak Periods', desc: 'Shipping rates fluctuate seasonally. The platform&apos;s historical data shows that rates are typically lowest during the first quarter of the year and highest during the holiday season from October through December. Planning your bulk orders during off-peak periods can result in substantial savings.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-accent-50 rounded-lg p-6 md:p-8 border border-accent-100">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">Calculate Your Shipping Costs</h2>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer">
                    Start Calculating
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                  </a>
                  <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                    Back to Homepage
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}